'use client'

import {
  compExpiryNoticeMessage,
  type CompExpiryNoticeState,
} from '@/lib/billing/comp-expiry-notice'

export type CompExpiryBannerData = {
  daysLeft: number
  endsAtIso: string
  planName: string
  urgent: boolean
}

/**
 * "Your complimentary Pro access ends in 5 days" — shown site-wide over the
 * last week of an admin-granted plan.
 *
 * NOT DISMISSIBLE, for the same reason PastDueBanner is not: there is a
 * deadline, and missing it means losing access that is currently working.
 *
 * The action is /pricing, NOT the Stripe billing portal. A comped user has no
 * Stripe customer at all — every one of the twenty live grants had none — so
 * the portal would open on nothing. That is also why the copy asks them to
 * choose a plan rather than to "update payment", which is the past-due wording
 * and would be nonsense here.
 *
 * Amber, never red, until the last day. This is a gift running out, not a
 * failure on their part, and the tone should not imply they did something
 * wrong.
 */
export default function CompExpiryBanner({ data }: { data: CompExpiryBannerData | null }) {
  if (!data) return null

  const state: CompExpiryNoticeState = {
    daysLeft: data.daysLeft,
    endsAt: new Date(data.endsAtIso),
    planName: data.planName,
    urgent: data.urgent,
  }

  return (
    <div
      role="status"
      className={
        data.urgent
          ? 'w-full border-b border-amber-400 bg-amber-100 text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/50 dark:text-amber-100'
          : 'w-full border-b border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100'
      }
    >
      <div className="mx-auto flex w-full max-w-[1100px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 text-sm">
        <span className="font-medium">{compExpiryNoticeMessage(state)}</span>
        <span className="opacity-80">
          Your scores, streak and study plan stay saved either way.
        </span>
        <a
          href="/pricing"
          className="ml-auto rounded-md bg-foreground px-3 py-1 text-xs font-semibold text-background"
        >
          Keep {data.planName}
        </a>
      </div>
    </div>
  )
}
